import type { CannedResponse } from 'interfaces/canned-responses';

/**
 * Normalizes an email address for comparison by trimming whitespace and converting to lowercase.
 *
 * @param email - The email address to normalize (may be undefined)
 * @returns Normalized email string, or empty string if input is undefined/null
 */
const normalizeEmail = (email?: string): string => (email ? email.trim().toLowerCase() : '');

/**
 * Determines if an agent can access (view/manage) a canned response based on restrictions.
 *
 * **IMPORTANT: This is for MANAGEMENT/UI filtering only.**
 * Do NOT apply to chat/usage paths - per AC-1520, restrictions only affect management, not usage.
 *
 * Access control rules:
 * - Unrestricted agents: Can access all responses
 * - Restricted agents: Can only access private responses they created
 *
 * Email comparison is case-insensitive and whitespace-trimmed to handle variations.
 * Responses without createdBy field (legacy data) are not accessible to restricted agents.
 *
 * @param response - The canned response to check access for
 * @param isRestricted - Whether the current agent has restricted access
 * @param loggedAgentEmail - The email of the logged-in agent
 * @returns true if the agent can access the response, false otherwise
 */
export const canAccessCannedResponse = (
  response: CannedResponse,
  isRestricted: boolean,
  loggedAgentEmail: string | undefined,
): boolean => {
  // Unrestricted agents can access all responses
  if (!isRestricted) {
    return true;
  }

  // Restricted agents can only access private responses they created
  // Reject responses without createdBy (legacy data)
  if (!response.isPrivate || !response.createdBy || !loggedAgentEmail) {
    return false;
  }

  const createdBy = normalizeEmail(response.createdBy);
  const logged = normalizeEmail(loggedAgentEmail);

  // Reject if either email becomes empty after normalization
  if (!createdBy || !logged) {
    return false;
  }

  return createdBy === logged;
};

/**
 * Filters an array of canned responses based on access restrictions.
 *
 * Optimized to normalize loggedAgentEmail once rather than on every iteration.
 *
 * @param responses - Array of canned responses to filter
 * @param isRestricted - Whether the current agent has restricted access
 * @param loggedAgentEmail - The email of the logged-in agent
 * @returns Filtered array of canned responses the agent can access
 */
export const filterCannedResponsesByAccess = (
  responses: CannedResponse[],
  isRestricted: boolean,
  loggedAgentEmail: string | undefined,
): CannedResponse[] => {
  if (!isRestricted) {
    return responses;
  }

  // Normalize once to avoid repeated trim/toLowerCase in loop
  const normalizedLoggedEmail = normalizeEmail(loggedAgentEmail);

  return responses.filter((response) => {
    // Restricted agents can only access private responses they created
    if (!response.isPrivate || !response.createdBy) {
      return false;
    }

    const normalizedCreatedBy = normalizeEmail(response.createdBy);

    // Both emails must be valid (non-empty after normalization) and equal
    return normalizedCreatedBy && normalizedLoggedEmail && normalizedCreatedBy === normalizedLoggedEmail;
  });
};
